import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailPosWorkstations, type PosWorkstation } from '@/api/retailPosWorkstations';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'retail.workstation.manage';

const TYPE_LABEL: Record<string, string> = {
  desktop: 'Стаціонарний',
  mobile: 'Мобільний',
  tablet: 'Планшет',
};

export default function PosWorkstationsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-pos-workstations', orgId],
    queryFn: () => retailPosWorkstations.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<PosWorkstation>[]>(
    () => [
      { key: 'code', header: 'Код', width: 150, sortValue: (r) => r.code ?? '', cell: (r) => r.code },
      { key: 'name', header: 'Назва', sortValue: (r) => r.name ?? '', cell: (r) => r.name },
      {
        key: 'workstation_type',
        header: 'Тип',
        width: 140,
        sortValue: (r) => TYPE_LABEL[r.workstation_type] ?? r.workstation_type,
        cell: (r) => TYPE_LABEL[r.workstation_type] ?? r.workstation_type,
      },
      {
        key: 'is_active',
        header: 'Активний',
        width: 120,
        align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0),
        cell: (r) => (r.is_active ? '✓' : '—'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав керувати POS-станціями (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          POS-станції
          <HintBadge
            title="Що таке POS-станція?"
            text={
              'POS-станція (Point of Sale) — це конкретне «робоче місце касира»: ' +
              'комп\'ютер, планшет або телефон, з якого пробивається чеки.\n\n' +
              'Кожна станція має прив\'язки:\n' +
              '• Склад — звідки списуються товари при продажу.\n' +
              '• Каса — куди йде готівка (Дт 301).\n' +
              '• ПРРО-провайдер — для фіскалізації чеків.\n' +
              '• Цінова група — який прайс показується касиру.\n\n' +
              'Касир обирає свою станцію при першому вході у /pos (зберігається у браузері). ' +
              'Один магазин може мати кілька POS-станцій (наприклад «Каса 1» і «Каса 2»).'
            }
          />
        </h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {o.code}
            </option>
          ))}
        </select>
        <button
          className="primary"
          disabled={!canCreate}
          title={
            canCreate
              ? 'Відкрити Майстра підключення POS-станції (6 кроків: юрособа → каса → склад → ціни → ПРРО → POS)'
              : tooltipNew
          }
          onClick={() => navigate('/retail/setup-wizard')}
        >
          🧙 + POS-станція (Майстер)
        </button>
      </div>
      <DataTable<PosWorkstation>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає POS-станцій."
        onRowDoubleClick={(r) => navigate(`/retail/pos-workstations/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="code"
      />
    </>
  );
}
